import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getProfilePicture } from '../utils/profilePicture';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { isAdminOrSuperAdmin, isUberAdmin } from '../utils/auth';
import '../App.css';

interface User {
  id: string;
  email?: string;
  firstName: string;
  lastName: string;
  profileType: string;
  profilePicture?: string;
  dateOfBirth?: string;
  dateOfBirthYear?: number; // For privacy: only year shown to unauthorized viewers
  phoneNumber?: string;
  address?: string;
  soccerExperience?: string;
  allowedLeagueIds?: string[];
  teams?: Array<{
    teamId: string;
    teamName?: string;
    teamNumber?: string;
    role: string;
    position?: string;
    jerseyNumber?: number;
    isGuest: boolean;
    joinedAt: string;
  }>;
  statistics?: Array<{
    teamId: string;
    matchesPlayed: number;
    goals: number;
    assists: number;
    yellowCards: number;
    redCards: number;
  }>;
}

interface League {
  id: string;
  name: string;
}

const UserProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user: currentUser, logout } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    profilePicture: '',
    phoneNumber: '',
    address: '',
    email: '',
    soccerExperience: ''
  });
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [resettingPassword, setResettingPassword] = useState(false);
  const [profilePicturePreview, setProfilePicturePreview] = useState<string>('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [selectedLeagues, setSelectedLeagues] = useState<string[]>([]);
  const [savingLeagues, setSavingLeagues] = useState(false);
  const [changingRole, setChangingRole] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [savingRole, setSavingRole] = useState(false);
  const [assignmentOptions, setAssignmentOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedAssignments, setSelectedAssignments] = useState<string[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  const canEdit = currentUser && (currentUser.id === id || isAdminOrSuperAdmin(currentUser.profileType));
  const isAdmin = isAdminOrSuperAdmin(currentUser?.profileType);
  const isSuperAdminUser = isUberAdmin(currentUser?.profileType);
  const isReferee = user?.profileType === 'referee';

  // Check if current user can view sensitive data
  // Note: Backend also checks if user is a manager of teams the profile user is on
  const canViewSensitiveData = currentUser && (
    currentUser.id === id ||
    isAdminOrSuperAdmin(currentUser.profileType) ||
    currentUser.profileType === 'manager'
  );

  useEffect(() => {
    const fetchUser = async () => {
      try {
        // Fetch user data
        const userResponse = await api.get(`/users/${id}`);
        let userData = userResponse.data;
        
        // If user is a player, fetch additional player data (teams and stats)
        if (userData.profileType === 'player') {
          try {
            const playerResponse = await api.get(`/players/${id}`);
            userData = {
              ...userData,
              teams: playerResponse.data.teams || [],
              statistics: playerResponse.data.statistics || [],
              // Preserve sensitive data from player endpoint if available
              phoneNumber: playerResponse.data.phoneNumber || userData.phoneNumber,
              address: playerResponse.data.address || userData.address,
              dateOfBirth: playerResponse.data.dateOfBirth || userData.dateOfBirth,
              dateOfBirthYear: playerResponse.data.dateOfBirthYear || userData.dateOfBirthYear
            };
          } catch (error) {
            // If player endpoint fails, just use basic user data
            console.error('Error fetching player data:', error);
          }
        }
        
        setUser(userData);
        setFormData({
          profilePicture: userData.profilePicture || '',
          phoneNumber: userData.phoneNumber || '',
          address: userData.address || '',
          email: userData.email || '',
          soccerExperience: userData.soccerExperience || ''
        });
        setProfilePicturePreview(getProfilePicture(userData));
        
        // If user is a referee and current user is admin, fetch leagues
        if (userData.profileType === 'referee' && isAdminOrSuperAdmin(currentUser?.profileType)) {
          try {
            const leaguesResponse = await api.get('/leagues');
            const allLeagues = leaguesResponse.data.leagues || leaguesResponse.data || [];
            setLeagues(allLeagues);
            
            // Set selected leagues - if allowedLeagueIds is undefined or empty, select all
            if (userData.allowedLeagueIds && userData.allowedLeagueIds.length > 0) {
              setSelectedLeagues(userData.allowedLeagueIds);
            } else {
              setSelectedLeagues(allLeagues.map((l: League) => l.id));
            }
          } catch (error) {
            console.error('Error fetching leagues:', error);
          }
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchUser();
    }
  }, [id, currentUser]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setError('Image size must be less than 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setFormData({
          ...formData,
          profilePicture: base64String
        });
        setProfilePicturePreview(base64String);
        setError('');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSave = async () => {
    setError('');
    setSaving(true);

    try {
      const response = await api.put(`/users/${id}`, formData);
      setUser(response.data);
      setEditing(false);
      // Update profile picture preview
      setProfilePicturePreview(getProfilePicture(response.data));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        profilePicture: user.profilePicture || '',
        phoneNumber: user.phoneNumber || '',
        address: user.address || '',
        email: user.email || '',
        soccerExperience: user.soccerExperience || ''
      });
      setProfilePicturePreview(getProfilePicture(user));
    }
    setEditing(false);
    setError('');
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handlePasswordReset = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    setError('');
    setResettingPassword(true);

    try {
      await api.post(`/users/${id}/reset-password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      setShowPasswordReset(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      alert('Password reset successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!user) {
    return <div className="container">User not found</div>;
  }

  const getProfileTypeLabel = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ position: 'relative' }}>
              <img 
                src={profilePicturePreview} 
                alt={`${user.firstName} ${user.lastName}`}
                style={{ width: '120px', height: '120px', borderRadius: '50%', objectFit: 'cover' }}
              />
              {editing && (
                <label
                  style={{
                    position: 'absolute',
                    bottom: '0',
                    right: '0',
                    background: '#2E7D32',
                    color: 'white',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '18px'
                  }}
                  title="Change profile picture"
                >
                  📷
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </label>
              )}
            </div>
            <div>
              <h1 style={{ margin: 0 }}>{user.firstName} {user.lastName}</h1>
              <div style={{ color: '#757575', margin: '5px 0' }}>
                {user.email && <>{user.email} • </>}<span className="badge badge-info">{getProfileTypeLabel(user.profileType)}</span>
                {isSuperAdminUser && currentUser?.id !== id && (
                  <>
                    {!changingRole ? (
                      <button
                        className="btn btn-secondary"
                        style={{ marginLeft: '10px', fontSize: '12px', padding: '4px 10px' }}
                        onClick={() => { setChangingRole(true); setNewRole(user.profileType); setAssignmentOptions([]); setSelectedAssignments([]); }}
                      >
                        Change Role
                      </button>
                    ) : (
                      <div style={{ marginLeft: '10px', display: 'inline-block' }}>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: assignmentOptions.length > 0 ? '10px' : '0' }}>
                          <select
                            value={newRole}
                            onChange={async (e) => {
                              const role = e.target.value;
                              setNewRole(role);
                              setSelectedAssignments([]);
                              setAssignmentOptions([]);

                              if (role === 'admin') {
                                setLoadingAssignments(true);
                                try {
                                  const res = await api.get('/leagues', { params: { page: '1', limit: '100' } });
                                  const items = res.data.leagues || res.data || [];
                                  setAssignmentOptions(items.map((l: any) => ({ id: l.id, name: l.name })));
                                } catch { setAssignmentOptions([]); }
                                setLoadingAssignments(false);
                              } else if (role === 'director') {
                                setLoadingAssignments(true);
                                try {
                                  const res = await api.get('/clubs');
                                  const items = res.data.clubs || res.data || [];
                                  setAssignmentOptions(items.map((c: any) => ({ id: c.id, name: c.name })));
                                } catch { setAssignmentOptions([]); }
                                setLoadingAssignments(false);
                              } else if (role === 'manager') {
                                setLoadingAssignments(true);
                                try {
                                  const res = await api.get('/teams', { params: { page: '1', limit: '100' } });
                                  const items = res.data.teams || res.data || [];
                                  setAssignmentOptions(items.map((t: any) => ({ id: t.id, name: t.name })));
                                } catch { setAssignmentOptions([]); }
                                setLoadingAssignments(false);
                              }
                            }}
                            style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #E0E0E0', fontSize: '13px' }}
                          >
                            <option value="player">Player</option>
                            <option value="manager">Manager</option>
                            <option value="director">Director</option>
                            <option value="admin">Admin</option>
                            <option value="super_admin">Super Admin</option>
                            <option value="referee">Referee</option>
                            <option value="parent">Parent</option>
                          </select>
                          <button
                            className="btn btn-primary"
                            style={{ fontSize: '12px', padding: '4px 10px' }}
                            disabled={savingRole || newRole === user.profileType}
                            onClick={async () => {
                              setSavingRole(true);
                              try {
                                const payload: any = { profileType: newRole };
                                if (selectedAssignments.length > 0) {
                                  payload.assignments = selectedAssignments;
                                }
                                await api.patch(`/users/${id}/profile-type`, payload);
                                setUser({ ...user, profileType: newRole });
                                setChangingRole(false);
                                setAssignmentOptions([]);
                                setSelectedAssignments([]);
                              } catch (err: any) {
                                alert(err.response?.data?.error || 'Failed to change role');
                              } finally {
                                setSavingRole(false);
                              }
                            }}
                          >
                            {savingRole ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            className="btn btn-secondary"
                            style={{ fontSize: '12px', padding: '4px 10px' }}
                            onClick={() => { setChangingRole(false); setAssignmentOptions([]); setSelectedAssignments([]); }}
                          >
                            Cancel
                          </button>
                        </div>
                        {loadingAssignments && <p style={{ fontSize: '13px', color: '#757575' }}>Loading...</p>}
                        {assignmentOptions.length > 0 && (
                          <div style={{ background: '#F5F5F5', padding: '12px', borderRadius: '8px', maxWidth: '400px' }}>
                            <p style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>
                              {newRole === 'admin' && 'Select leagues this admin will govern:'}
                              {newRole === 'director' && 'Select clubs this director will manage:'}
                              {newRole === 'manager' && 'Select teams this manager will manage:'}
                            </p>
                            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                              {assignmentOptions.map(opt => (
                                <label key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '4px 0' }}>
                                  <input
                                    type="checkbox"
                                    checked={selectedAssignments.includes(opt.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedAssignments([...selectedAssignments, opt.id]);
                                      } else {
                                        setSelectedAssignments(selectedAssignments.filter(a => a !== opt.id));
                                      }
                                    }}
                                  />
                                  <span style={{ fontSize: '13px' }}>{opt.name}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {currentUser && currentUser.id === id && (
              <Link to="/payments" className="btn btn-secondary">
                My Payments
              </Link>
            )}
            {canEdit && !editing && (
              <button className="btn btn-primary" onClick={() => setEditing(true)}>
                Edit Profile
              </button>
            )}
            {currentUser && currentUser.id === id && (
              <button className="btn btn-danger" onClick={async () => { await logout(); window.location.href = '/login'; }}>
                Logout
              </button>
            )}
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <div style={{ marginTop: '30px' }}>
          <h2>Personal Information</h2>
          {editing ? (
            <div className="form">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="user@example.com"
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="form-group">
                <label>Address</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Street address, City, State, ZIP"
                />
              </div>
              {user.profileType === 'player' && (
                <div className="form-group">
                  <label>Soccer Experience</label>
                  <select
                    name="soccerExperience"
                    value={formData.soccerExperience}
                    onChange={handleChange}
                  >
                    <option value="">Select experience level</option>
                    <option value="Beginner">Beginner</option>
                    <option value="Recreational">Recreational</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                    <option value="Semi-Professional">Semi-Professional</option>
                    <option value="Professional">Professional</option>
                  </select>
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button className="btn btn-secondary" onClick={handleCancel} disabled={saving}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <p>
                <strong>Date of Birth:</strong>{' '}
                {canViewSensitiveData && user.dateOfBirth 
                  ? new Date(user.dateOfBirth).toLocaleDateString()
                  : user.dateOfBirthYear 
                    ? `${user.dateOfBirthYear}`
                    : 'N/A'}
              </p>
              {canViewSensitiveData && (
                <>
                  <p><strong>Phone:</strong> {user.phoneNumber || 'N/A'}</p>
                  <p><strong>Address:</strong> {user.address || 'N/A'}</p>
                </>
              )}
              {user.soccerExperience && (
                <p><strong>Soccer Experience:</strong> {user.soccerExperience}</p>
              )}
            </>
          )}
        </div>

        {isAdmin && isReferee && (
          <div style={{ marginTop: '30px' }}>
            <h2>Allowed Leagues</h2>
            <p style={{ color: '#757575', marginBottom: '15px' }}>
              Select which leagues this referee can officiate matches for.
            </p>
            {leagues.length > 0 ? (
              <div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <input
                      type="checkbox"
                      checked={selectedLeagues.length === leagues.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedLeagues(leagues.map(l => l.id));
                        } else {
                          setSelectedLeagues([]);
                        }
                      }}
                    />
                    <strong>Select All</strong>
                  </label>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '10px', marginBottom: '15px' }}>
                  {leagues.map(league => (
                    <label key={league.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selectedLeagues.includes(league.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedLeagues([...selectedLeagues, league.id]);
                          } else {
                            setSelectedLeagues(selectedLeagues.filter(id => id !== league.id));
                          }
                        }}
                      />
                      <span>{league.name}</span>
                    </label>
                  ))}
                </div>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    setSavingLeagues(true);
                    setError('');
                    try {
                      await api.patch(`/users/${id}/allowed-leagues`, {
                        allowedLeagueIds: selectedLeagues
                      });
                      // Update user state
                      setUser({ ...user!, allowedLeagueIds: selectedLeagues });
                      alert('Referee league assignments updated successfully');
                    } catch (err: any) {
                      setError(err.response?.data?.error || 'Failed to update league assignments');
                    } finally {
                      setSavingLeagues(false);
                    }
                  }}
                  disabled={savingLeagues}
                >
                  {savingLeagues ? 'Saving...' : 'Save League Assignments'}
                </button>
              </div>
            ) : (
              <p style={{ color: '#757575' }}>No leagues available.</p>
            )}
          </div>
        )}

        {canEdit && !editing && (
          <div style={{ marginTop: '30px' }}>
            <h2>Password</h2>
            {!showPasswordReset ? (
              <button className="btn btn-secondary" onClick={() => setShowPasswordReset(true)}>
                Reset Password
              </button>
            ) : (
              <div className="form" style={{ maxWidth: '500px' }}>
                <div className="form-group">
                  <label>Current Password *</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>New Password *</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                    minLength={6}
                  />
                  <small style={{ color: '#757575' }}>Must be at least 6 characters</small>
                </div>
                <div className="form-group">
                  <label>Confirm New Password *</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                    minLength={6}
                  />
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button 
                    className="btn btn-primary" 
                    onClick={handlePasswordReset} 
                    disabled={resettingPassword}
                  >
                    {resettingPassword ? 'Resetting...' : 'Reset Password'}
                  </button>
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => {
                      setShowPasswordReset(false);
                      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                      setError('');
                    }}
                    disabled={resettingPassword}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {user.profileType === 'player' && user.teams && user.teams.length > 0 && (
          <div style={{ marginTop: '30px' }}>
            <h2>Team Memberships ({user.teams.length})</h2>
            <table className="table">
              <thead>
                <tr>
                  <th>Team</th>
                  <th>Role</th>
                  <th>Position</th>
                  <th>Jersey #</th>
                  <th>Type</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {user.teams.map((team, idx) => (
                  <tr key={idx}>
                    <td>
                      <Link to={`/teams/${team.teamId}`}>
                        {team.teamName || 'Unknown'} ({team.teamNumber || 'N/A'})
                      </Link>
                    </td>
                    <td>
                      <span className={`badge ${team.role === 'captain' ? 'badge-info' : 'badge-secondary'}`}>
                        {team.role}
                      </span>
                    </td>
                    <td>{team.position || '-'}</td>
                    <td>{team.jerseyNumber || '-'}</td>
                    <td>
                      {team.isGuest ? (
                        <span className="badge badge-warning">Guest</span>
                      ) : (
                        <span className="badge badge-success">Regular</span>
                      )}
                    </td>
                    <td>{new Date(team.joinedAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {user.profileType === 'player' && user.statistics && user.statistics.length > 0 && (
          <div style={{ marginTop: '30px' }}>
            <h2>Statistics</h2>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
              {user.statistics.map((stat, idx) => {
                const team = user.teams?.find(t => t.teamId === stat.teamId);
                return (
                  <div key={idx} className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
                      <h4 style={{ margin: 0 }}>
                        <Link to={`/teams/${stat.teamId}`} style={{ color: '#2E7D32', textDecoration: 'none' }}>
                          {team?.teamName || 'Unknown Team'}
                        </Link>
                      </h4>
                      {team?.teamNumber && (
                        <span style={{ color: '#757575', fontSize: '12px' }}>{team.teamNumber}</span>
                      )}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <div><strong>Matches:</strong> {stat.matchesPlayed}</div>
                      <div><strong>Goals:</strong> {stat.goals}</div>
                      <div><strong>Assists:</strong> {stat.assists}</div>
                      <div><strong>Yellow Cards:</strong> {stat.yellowCards}</div>
                      <div><strong>Red Cards:</strong> {stat.redCards}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;

